import { Component, Input, OnInit, DoCheck } from '@angular/core';
import { SurveyService } from '../../../services/surveys.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-survey-custom-charts',
  standalone: false,
  templateUrl: './survey-custom-charts.component.html',
  styleUrls: ['./survey-custom-charts.component.scss'],
})
export class SurveyCustomChartsComponent implements OnInit, DoCheck {
  @Input() survey: any;

  charts: any[] = [];
  loading = false;
  creating = false;

  questionDimensions: any[] = [];
  customFieldDimensions: any[] = [];

  newChartTitle = '';
  newChartType = 'bar';
  selectedXAxis: any = null;
  selectedGroupBy: any = null;

  chartDataMap: { [id: string]: any } = {};

  private _lastSig = '';

  constructor(
    private surveyService: SurveyService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.extractDimensions();
    this.loadCharts();
  }

  ngDoCheck() {
    const qLen = this.survey?.questions?.length || 0;
    const cfLen = this.survey?.team_member_custom_fields?.length || 0;
    const sig = `${qLen}_${cfLen}`;
    if (sig !== this._lastSig && (qLen > 0 || cfLen > 0)) {
      this._lastSig = sig;
      this.extractDimensions();
    }
  }

  extractDimensions() {
    this.questionDimensions = [];
    this.customFieldDimensions = [];

    if (this.survey?.team_member_custom_fields) {
      this.survey.team_member_custom_fields.forEach((cf: any) => {
        this.customFieldDimensions.push({ label: cf.name, value: { type: 'custom_field', id: cf.id } });
      });
    }

    if (this.survey?.questions) {
      this.survey.questions.forEach((q: any) => {
        if (['CHECKBOX', 'MULTIPLE_CHOICE', 'QUANTITATIVE', 'CONSENSUS'].includes(q.typology)) {
           this.questionDimensions.push({ label: q.question, value: { type: 'question', id: q.id } });
        }
      });
    }
  }

  loadCharts() {
    if (!this.survey?.id) return;
    this.loading = true;
    this.surveyService.getCustomCharts(this.survey.id, (res) => {
      this.charts = res;
      this.loading = false;
      this.charts.forEach(c => this.loadChartData(c));
    }, () => this.loading = false);
  }

  loadChartData(chart: any) {
    this.surveyService.getCustomChartData(this.survey.id, chart.id, (res: any) => {

      const mappedSeries = res.series.map((s: any) => {
        if (chart.chart_type !== 'pie') {
          s.type = chart.chart_type; // override backend bar type if line or scatter
        }
        return s;
      });

      this.chartDataMap[chart.id] = {
        tooltip: { trigger: 'axis' },
        legend: { data: mappedSeries.map((s: any) => s.name) },
        xAxis: chart.chart_type === 'pie' ? undefined : { type: 'category', data: res.categories },
        yAxis: chart.chart_type === 'pie' ? undefined : { type: 'value' },
        series: mappedSeries
      };
      if (chart.chart_type === 'pie') {
        this.chartDataMap[chart.id].tooltip.trigger = 'item';
      }
    });
  }

  createNewChart() {
    if (!this.newChartTitle || !this.selectedXAxis) return;
    this.creating = true;

    const payload = {
      title: this.newChartTitle,
      chart_type: this.newChartType,
      settings: {
        x_axis: this.selectedXAxis,
        group_by: this.selectedGroupBy || null
      }
    };

    this.surveyService.createCustomChart(this.survey.id, payload, () => {
      this.creating = false;
      this.newChartTitle = '';
      this.selectedXAxis = null;
      this.selectedGroupBy = null;
      this.loadCharts();
    }, () => this.creating = false);
  }

  deleteChart(id: any) {
    if (confirm(this.translateService.instant('components.custom-charts.delete-confirm'))) {
      this.surveyService.deleteCustomChart(this.survey.id, id, () => {
        this.loadCharts();
      });
    }
  }
}
